// La vie au travail : collègues, formations, primes, heures en plus.
import type { EventDef } from '../events';
import type { ExpenseId, GameState } from '../../engine/state';
import { MONTHLY_HOURS, OVERTIME_BONUS } from '../../game/economy';
import { between, simpleEvent, working } from './kit';

/** Jours écoulés depuis le premier emploi (0 avant). */
function seniority(state: GameState): number {
  return state.firstJobDay === null ? 0 : state.day - state.firstJobDay;
}

/** Les deux années d'alternance, pendant le prologue. */
function alternant(state: GameState): boolean {
  return state.path === 'alternance';
}

/** Une part du salaire, arrondie à l'euro. */
function ofSalary(state: GameState, share: number): number {
  return Math.round(state.salary * share);
}

/** Les trajets coûtent en carburant avec une voiture, en transports en commun sinon. */
function commute(state: GameState): Extract<ExpenseId, 'carburant' | 'transport'> {
  return state.hasCar ? 'carburant' : 'transport';
}

/** La mission du week-end et ses cotisations, citées par deux événements. */
const FREELANCE_GAIN = 400;
const FREELANCE_DUES = 100;

export const EVENTS: EventDef[] = [
  // L'alternance, pendant le prologue

  simpleEvent({
    id: 'alternance_inventaire',
    kind: 'travail',
    stages: ['prologue'],
    season: [12, 1],
    cooldownDays: 300,
    condition: alternant,
    params: (state) => ({ gain: between(state, 40, 60) }),
    choices: [
      { id: 'venir', effects: (_, p) => ({ cash: p.gain, energy: -4 }) },
      { id: 'refuser', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'alternance_lecon_devis',
    kind: 'travail',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternant,
    choices: [
      { id: 'rester', effects: { energy: -3, xp: { skill: 'technique', amount: 15 } } },
      { id: 'notes', effects: { energy: -1, xp: { skill: 'technique', amount: 5 } } },
      { id: 'demain' },
    ],
  }),
  simpleEvent({
    id: 'alternance_prime',
    kind: 'chance',
    stages: ['prologue'],
    maxCount: 2,
    condition: alternant,
    params: (state) => ({ gain: between(state, 80, 150), cost: between(state, 20, 35) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'garder', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'cafe', effects: (_, p) => ({ cash: p.gain - p.cost, wellbeing: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'alternance_tenue',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternant,
    params: (state) => ({ cost: between(state, 90, 150), low: between(state, 20, 40) }),
    choices: [
      { id: 'neuve', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'friperie', effects: (_, p) => ({ cash: -p.low, energy: -2, wellbeing: 1 }) },
      { id: 'emprunter' },
    ],
  }),
  simpleEvent({
    id: 'alternance_portes_ouvertes',
    kind: 'travail',
    stages: ['prologue'],
    season: [1, 2, 3],
    cooldownDays: 300,
    condition: alternant,
    choices: [
      { id: 'preparer', effects: { energy: -3, xp: { skill: 'communication', amount: 15 } } },
      { id: 'improviser', effects: { energy: -1, xp: { skill: 'communication', amount: 8 } } },
      { id: 'decliner', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'alternance_proposition',
    kind: 'travail',
    stages: ['prologue'],
    weight: 4,
    maxCount: 1,
    // Les huit derniers mois de l'alternance.
    condition: (state) =>
      alternant(state) && state.firstJobTargetDay !== null && state.firstJobTargetDay - state.day <= 240,
    choices: [
      { id: 'accepter', effects: { wellbeing: 4 } },
      { id: 'comparer', effects: { energy: -4, xp: { skill: 'communication', amount: 20 } } },
    ],
  }),

  // Le salaire, les primes et les heures en plus

  simpleEvent({
    id: 'entretien_annuel',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    season: [1, 2],
    cooldownDays: 300,
    maxCount: 3,
    condition: (state) => working(state) && seniority(state) >= 180,
    params: (state) => ({ salary: ofSalary(state, 1.02) }),
    choices: [
      { id: 'augmentation', effects: { raise: 0.02, energy: -2, wellbeing: -1 } },
      { id: 'formation', effects: (state) => ({ xp: { skill: state.focusSkill, amount: 25 } }) },
      { id: 'teletravail', effects: { wellbeing: 3 } },
    ],
  }),
  simpleEvent({
    id: 'heures_supplementaires',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: working,
    // Dix heures payées 25 % de plus qu'une heure normale.
    params: (state) => {
      const gain = Math.round((state.salary / MONTHLY_HOURS) * OVERTIME_BONUS * 10);
      return { gain, half: Math.round(gain / 2) };
    },
    choices: [
      { id: 'accepter', effects: (_, p) => ({ cash: p.gain, energy: -5, wellbeing: -2 }) },
      { id: 'moitie', effects: (_, p) => ({ cash: p.half, energy: -2, wellbeing: -1 }) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'interessement',
    kind: 'chance',
    pleasureCategory: 'voyages',
    stages: ['stage1', 'stage2'],
    season: [4, 5],
    cooldownDays: 300,
    maxCount: 3,
    condition: (state) => working(state) && seniority(state) >= 90,
    params: (state) => ({
      gain: between(state, ofSalary(state, 0.2), ofSalary(state, 0.35)),
      cost: between(state, 120, 200),
    }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'weekend', effects: (_, p) => ({ cash: p.gain - p.cost, pleasure: 4 }) },
    ],
  }),
  simpleEvent({
    id: 'rappel_salaire',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    condition: (state) => working(state) && seniority(state) >= 120,
    params: (state) => ({
      gain: between(state, ofSalary(state, 0.05), ofSalary(state, 0.1)),
      cost: between(state, 30, 50),
    }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'diner', effects: (_, p) => ({ cash: p.gain - p.cost, pleasure: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'trop_percu_paie',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 2,
    condition: working,
    params: (state) => ({ cost: between(state, ofSalary(state, 0.1), ofSalary(state, 0.2)) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      // Une retenue étalée, sans intérêts.
      { id: 'etaler', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0, months: 3 } }) },
    ],
  }),
  simpleEvent({
    id: 'activite_partielle',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 1,
    condition: working,
    params: (state) => {
      const cost = ofSalary(state, 0.12);
      return { cost, half: Math.round(cost / 2) };
    },
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'extras', effects: (_, p) => ({ cash: -p.half, energy: -5 }) },
    ],
  }),
  simpleEvent({
    id: 'prime_envolee',
    kind: 'travail',
    pleasureCategory: 'voyages',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 1,
    condition: (state) => working(state) && seniority(state) >= 180,
    params: (state) => {
      const cost = between(state, 200, 350);
      return { cost, fee: Math.round(cost * 0.3) };
    },
    choices: [
      { id: 'partir', effects: (_, p) => ({ cash: -p.cost, pleasure: 3 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost, pleasure: 3 }) },
      { id: 'annuler', effects: (_, p) => ({ cash: -p.fee, wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'mission_freelance',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: () => ({ gain: FREELANCE_GAIN }),
    choices: [
      {
        id: 'accepter',
        effects: (_, p) => ({ cash: p.gain, energy: -6, wellbeing: -2, flag: 'missionFreelance' }),
      },
      { id: 'refuser' },
    ],
  }),
  // La suite de la mission : tirée au sort, pour respecter l'écart entre deux coups durs.
  simpleEvent({
    id: 'cotisations_freelance',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 4,
    weight: 5,
    maxCount: 1,
    condition: (state) => working(state) && state.flags.missionFreelance === true,
    params: () => ({ gain: FREELANCE_GAIN, cost: FREELANCE_DUES, net: FREELANCE_GAIN - FREELANCE_DUES }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
    ],
  }),
  simpleEvent({
    id: 'projet_reussi',
    kind: 'chance',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    condition: (state) => working(state) && seniority(state) >= 180,
    params: (state) => ({
      gain: between(state, ofSalary(state, 0.15), ofSalary(state, 0.25)),
      salary: ofSalary(state, 1.01),
    }),
    choices: [
      { id: 'prime', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'hausse', effects: { raise: 0.01 } },
    ],
  }),
  simpleEvent({
    id: 'boite_a_idees',
    kind: 'chance',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ gain: between(state, 100, 250), cost: between(state, 50, 80) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'equipe', effects: (_, p) => ({ cash: p.gain - p.cost, wellbeing: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'offre_concurrent',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => working(state) && seniority(state) >= 365,
    params: (state) => ({ high: ofSalary(state, 1.05), mid: ofSalary(state, 1.02) }),
    choices: [
      { id: 'partir', effects: { raise: 0.05, energy: -4, wellbeing: -2 } },
      { id: 'rester', effects: { raise: 0.02, wellbeing: 1 } },
    ],
  }),

  // Se former et progresser

  simpleEvent({
    id: 'formation_certifiante',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    condition: working,
    // La participation qui reste à payer quand on utilise son compte formation.
    params: () => ({ cost: 100 }),
    choices: [
      {
        id: 'inscrire',
        effects: (_, p) => ({ cash: -p.cost, energy: -4, xp: { skill: 'organisation', amount: 30 } }),
      },
      { id: 'videos', effects: { energy: -2, xp: { skill: 'organisation', amount: 10 } } },
      { id: 'attendre' },
    ],
  }),
  simpleEvent({
    id: 'mentor_benali',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    choices: [
      {
        id: 'accepter',
        effects: {
          energy: -2,
          xp: { skill: 'communication', amount: 15 },
          followUp: { event: 'mentor_recommandation', days: 150 },
        },
      },
      { id: 'decliner' },
    ],
  }),
  // Cinq mois après avoir accepté le mentorat.
  simpleEvent({
    id: 'mentor_recommandation',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    manual: true,
    condition: working,
    params: (state) => ({ salary: ofSalary(state, 1.02) }),
    choices: [
      { id: 'accepter', effects: { raise: 0.02, energy: -6, wellbeing: -3 } },
      { id: 'refuser', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'stagiaire_paul',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    choices: [
      { id: 'encadrer', effects: { energy: -3, xp: { skill: 'communication', amount: 20 } } },
      { id: 'archives', effects: { wellbeing: 1, xp: { skill: 'organisation', amount: 5 } } },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'presentation_generale',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: working,
    choices: [
      { id: 'repeter', effects: { energy: -4, wellbeing: 2, xp: { skill: 'communication', amount: 25 } } },
      { id: 'improviser', effects: { wellbeing: -2, xp: { skill: 'communication', amount: 10 } } },
      { id: 'ines' },
    ],
  }),
  simpleEvent({
    id: 'tableur_automatise',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ cost: between(state, 10, 15) }),
    choices: [
      { id: 'macro', effects: { energy: -4, wellbeing: 1, xp: { skill: 'technique', amount: 25 } } },
      {
        id: 'thomas',
        effects: (_, p) => ({ cash: -p.cost, wellbeing: 1, xp: { skill: 'technique', amount: 8 } }),
      },
      { id: 'main', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'salon_lyon',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: working,
    params: (state) => ({ cost: between(state, 320, 450) }),
    choices: [
      {
        id: 'avancer',
        effects: (_, p) => ({
          cash: -p.cost,
          energy: -3,
          xp: { skill: 'communication', amount: 15 },
          followUp: { event: 'remboursement_salon', days: 30 },
        }),
      },
      { id: 'agence', effects: { energy: -5, wellbeing: -1, xp: { skill: 'communication', amount: 15 } } },
      { id: 'thomas' },
    ],
  }),
  // Un mois après le salon, si les frais ont été avancés.
  simpleEvent({
    id: 'remboursement_salon',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.cost, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.cost }) },
    ],
  }),

  // Les collègues et la vie de bureau

  simpleEvent({
    id: 'remplacement_ines',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: working,
    choices: [
      { id: 'reprendre', effects: { energy: -4, wellbeing: -1, xp: { skill: 'organisation', amount: 20 } } },
      { id: 'partager', effects: { energy: -2, xp: { skill: 'organisation', amount: 10 } } },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    id: 'idee_volee_thomas',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: working,
    choices: [
      { id: 'parler', effects: { energy: -2, wellbeing: 1, xp: { skill: 'communication', amount: 10 } } },
      { id: 'duval', effects: { energy: -1, wellbeing: 2 } },
      { id: 'laisser', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'projet_abandonne',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    condition: (state) => working(state) && seniority(state) >= 180,
    params: (state) => ({ cost: between(state, 18, 30) }),
    choices: [
      { id: 'bilan', effects: { energy: -2, xp: { skill: 'organisation', amount: 15 } } },
      { id: 'restaurant', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'passer', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'pot_depart_gerard',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ high: between(state, 15, 25), low: between(state, 5, 8) }),
    choices: [
      { id: 'genereux', effects: (_, p) => ({ cash: -p.high, wellbeing: 2 }) },
      { id: 'modeste', effects: (_, p) => ({ cash: -p.low, wellbeing: 1 }) },
      { id: 'carte' },
    ],
  }),
  simpleEvent({
    id: 'afterwork_equipe',
    kind: 'tentation',
    pleasureCategory: 'repas',
    stages: ['stage1', 'stage2'],
    condition: working,
    params: (state) => ({ cost: between(state, 25, 45), low: between(state, 6, 10) }),
    choices: [
      { id: 'rester', effects: (_, p) => ({ cash: -p.cost, pleasure: 4, energy: -3 }) },
      { id: 'verre', effects: (_, p) => ({ cash: -p.low, pleasure: 2, energy: -1 }) },
      { id: 'rentrer' },
    ],
  }),
  simpleEvent({
    id: 'resto_du_vendredi',
    kind: 'tentation',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ cost: between(state, 50, 65), low: between(state, 12, 16) }),
    choices: [
      { id: 'semaine', effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.cost }, wellbeing: 3 }) },
      { id: 'mois', effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.low }, wellbeing: 1 }) },
      { id: 'gamelle' },
    ],
  }),
  simpleEvent({
    id: 'titres_restaurant',
    kind: 'chance',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ save: between(state, 55, 70), low: between(state, 10, 15) }),
    choices: [
      { id: 'courses', effects: (_, p) => ({ addExpense: { id: 'courses', amount: -p.save } }) },
      {
        id: 'restaurant',
        effects: (_, p) => ({ addExpense: { id: 'courses', amount: -p.low }, wellbeing: 3 }),
      },
    ],
  }),
  simpleEvent({
    id: 'teletravail_chaise',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: working,
    params: (state) => ({ cost: between(state, 150, 250), low: between(state, 40, 70) }),
    choices: [
      { id: 'neuve', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.low, energy: -2, wellbeing: 2 }) },
      { id: 'pret', effects: { energy: -5 } },
    ],
  }),
  simpleEvent({
    id: 'badge_oublie',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    condition: working,
    params: (state) => ({ cost: between(state, 25, 40) }),
    choices: [
      { id: 'retour', effects: { energy: -2 } },
      { id: 'taxi', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'visiteur', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'cheques_vacances_cse',
    kind: 'travail',
    pleasureCategory: 'voyages',
    stages: ['stage1', 'stage2'],
    condition: working,
    // L'entreprise complète de 40 % ce que verse le salarié.
    params: (state) => {
      const cost = between(state, 150, 250);
      const bonus = Math.round((cost * 0.4) / 5) * 5;
      return { cost, bonus, total: cost + bonus };
    },
    choices: [
      { id: 'souscrire', effects: (_, p) => ({ cash: -p.cost, pleasure: 5 }) },
      { id: 'passer' },
    ],
  }),
  // Une location réservée pour l'été, avant que les congés soient validés.
  simpleEvent({
    id: 'conges_refuses',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 1,
    season: [3, 4],
    condition: working,
    params: (state) => ({ fee: between(state, 60, 110) }),
    choices: [
      { id: 'decaler', effects: (_, p) => ({ cash: -p.fee }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.fee }) },
      { id: 'echanger', effects: { energy: -2, wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'demenagement_bureaux',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: working,
    params: (state) => {
      const cost = between(state, 20, 35);
      return { cost, low: Math.round(cost / 2) };
    },
    choices: [
      {
        id: 'trajet',
        effects: (state, p) => ({ addExpense: { id: commute(state), amount: p.cost }, wellbeing: -2 }),
      },
      {
        id: 'teletravail',
        effects: (state, p) => ({ addExpense: { id: commute(state), amount: p.low }, energy: -3, wellbeing: 1 }),
      },
    ],
  }),

  // Les saisons du bureau

  simpleEvent({
    id: 'conges_a_poser',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    season: [4, 5],
    cooldownDays: 300,
    condition: working,
    choices: [
      { id: 'couper', effects: { wellbeing: 4, restDays: 5 } },
      { id: 'moitie', effects: { wellbeing: 2, restDays: 3, xp: { skill: 'organisation', amount: 5 } } },
      { id: 'filer', effects: { wellbeing: -3, xp: { skill: 'organisation', amount: 15 } } },
    ],
  }),
  simpleEvent({
    id: 'seminaire_rentree',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    season: [9],
    cooldownDays: 300,
    condition: working,
    params: (state) => ({ cost: between(state, 25, 40) }),
    choices: [
      {
        id: 'deguisement',
        effects: (_, p) => ({
          cash: -p.cost,
          energy: -3,
          wellbeing: 4,
          xp: { skill: 'communication', amount: 10 },
        }),
      },
      { id: 'bricoler', effects: { energy: -5, wellbeing: 3, xp: { skill: 'communication', amount: 10 } } },
      { id: 'coucher', effects: { energy: -1, wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'pere_noel_bureau',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    season: [12],
    cooldownDays: 300,
    maxCount: 2,
    condition: working,
    params: () => ({ cost: 15 }),
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'biscuits', effects: { energy: -2, wellbeing: 2 } },
      { id: 'passer', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'cheques_cadeaux_cse',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    season: [12],
    cooldownDays: 300,
    condition: working,
    params: (state) => ({ gain: between(state, 80, 170) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'plaisir', effects: { pleasure: 4 } },
    ],
  }),
];
